import type {
  CreatorBankAccount,
  CreatorReceivableSnapshot,
  CreatorPayment,
} from "../types";
import { CreatorPaymentStatus } from "../types";

const delay = (ms: number) =>
  new Promise((resolve) => setTimeout(resolve, ms));

/** Simula latência de rede em modo mock */
const MOCK_DELAY_MS = 400;

export const mockBankAccounts: CreatorBankAccount[] = [
  {
    id: "mock-bank-1",
    bank: "Nubank S.A.",
    code: "260",
    branch: "0001",
    account: "1234567-8",
    pix: "contato@creator.pro",
    active: true,
    default: true,
    createdAt: "2024-01-15T10:00:00Z",
    lastUpdatedAt: "2024-06-01T14:30:00Z",
  },
  {
    id: "mock-bank-2",
    bank: "Banco Itaú",
    code: "341",
    branch: "1234",
    account: "98765-4",
    pix: "creator@email.com",
    active: false,
    default: false,
    createdAt: "2023-11-01T09:00:00Z",
    lastUpdatedAt: "2024-02-10T11:00:00Z",
  },
];

/** Recebimentos acumulados: mesmo receivableGroupId = mesma NF-e e mesmo comprovante. */
const ACCUMULATED_GROUP_ID = "acc-nov-dez-jan";

export const mockReceivableSnapshots: CreatorReceivableSnapshot[] = [
  {
    paymentMonth: "2024-10",
    status: "CONCLUDED",
    currency: "BRL",
    grossAmount: 18500,
    netAmount: 17155,
    shareAmount: 15439.5,
    sharePercentage: 90,
    calculatedAt: "2024-11-05T12:00:00Z",
    nfeFileUrl: "https://example.com/nfe-out-2024-10.pdf",
    receiptFileUrl: "https://example.com/receipt-2024-10.pdf",
  },
  {
    paymentMonth: "2024-09",
    status: "CONCLUDED",
    currency: "BRL",
    grossAmount: 16400,
    netAmount: 15416,
    shareAmount: 13874.4,
    sharePercentage: 90,
    calculatedAt: "2024-10-05T12:00:00Z",
    nfeFileUrl: "https://example.com/nfe-2024-09.pdf",
    receiptFileUrl: null,
  },
  {
    paymentMonth: "2024-08",
    status: "CALCULATED",
    currency: "BRL",
    grossAmount: 12900,
    netAmount: 12126,
    shareAmount: 10913.4,
    sharePercentage: 90,
    calculatedAt: "2024-09-05T12:00:00Z",
  },
  {
    paymentMonth: "2024-07",
    status: "CONCLUDED",
    currency: "BRL",
    grossAmount: 14100,
    netAmount: 13254,
    shareAmount: 11928.6,
    sharePercentage: 90,
    calculatedAt: "2024-08-05T12:00:00Z",
    nfeFileUrl: "https://example.com/nfe-acc.pdf",
    receiptFileUrl: "https://example.com/receipt-acc.pdf",
    receivableGroupId: ACCUMULATED_GROUP_ID,
  },
  {
    paymentMonth: "2024-06",
    status: "WAITING",
    currency: "BRL",
    grossAmount: 11200,
    netAmount: 10528,
    shareAmount: 9475.2,
    sharePercentage: 90,
  },
  {
    paymentMonth: "2025-11",
    status: "CONCLUDED",
    currency: "BRL",
    grossAmount: 8000,
    netAmount: 7520,
    shareAmount: 69.27,
    sharePercentage: 1.16,
    calculatedAt: "2025-12-05T12:00:00Z",
    nfeFileUrl: "https://example.com/nfe-acc.pdf",
    receiptFileUrl: "https://example.com/receipt-acc.pdf",
    receivableGroupId: ACCUMULATED_GROUP_ID,
  },
  {
    paymentMonth: "2025-12",
    status: "CONCLUDED",
    currency: "BRL",
    grossAmount: 9000,
    netAmount: 8460,
    shareAmount: 39.53,
    sharePercentage: 1.83,
    calculatedAt: "2026-01-05T12:00:00Z",
    nfeFileUrl: "https://example.com/nfe-acc.pdf",
    receiptFileUrl: "https://example.com/receipt-acc.pdf",
    receivableGroupId: ACCUMULATED_GROUP_ID,
  },
  {
    paymentMonth: "2026-01",
    status: "CALCULATED",
    currency: "BRL",
    grossAmount: 10000,
    netAmount: 9400,
    shareAmount: 76.28,
    sharePercentage: 0.81,
    calculatedAt: "2026-02-05T12:00:00Z",
  },
];

export const mockPayments: CreatorPayment[] = [
  {
    id: "mock-payment-1",
    status: CreatorPaymentStatus.PAID,
    value: 15439.5,
    paymentDate: "2024-11-08",
    referencePaymentDate: "2024-10-01",
    bankAccount: {
      bank: "Nubank S.A.",
      account: "1234567-8",
      pix: "contato@creator.pro",
    },
    createdAt: "2024-11-05T14:00:00Z",
  },
  {
    id: "mock-payment-2",
    status: CreatorPaymentStatus.SCHEDULED,
    value: 13874.4,
    paymentDate: "2024-11-15",
    referencePaymentDate: "2024-09-01",
    bankAccount: {
      bank: "Nubank S.A.",
      account: "1234567-8",
      pix: "contato@creator.pro",
    },
    createdAt: "2024-10-10T10:00:00Z",
  },
  {
    id: "mock-payment-3",
    status: CreatorPaymentStatus.PENDING,
    value: 10913.4,
    paymentDate: "2024-12-05",
    referencePaymentDate: "2024-08-01",
    bankAccount: {
      bank: "Nubank S.A.",
      account: "1234567-8",
      pix: "contato@creator.pro",
    },
    createdAt: "2024-09-05T12:00:00Z",
  },
  {
    id: "mock-payment-4",
    status: CreatorPaymentStatus.ERROR,
    value: 5200,
    paymentDate: "2024-10-01",
    referencePaymentDate: "2024-07-01",
    bankAccount: {
      bank: "Banco Itaú",
      account: "98765-4",
      pix: "creator@email.com",
    },
    createdAt: "2024-08-20T09:00:00Z",
  },
];

export async function getMockBankAccounts(): Promise<CreatorBankAccount[]> {
  await delay(MOCK_DELAY_MS);
  return [...mockBankAccounts];
}

export async function getMockReceivableSnapshots(): Promise<{
  data: CreatorReceivableSnapshot[];
  page: number;
  limit: number;
  totalItems: number;
  totalPage: number;
}> {
  await delay(MOCK_DELAY_MS);
  return {
    data: [...mockReceivableSnapshots],
    page: 1,
    limit: 50,
    totalItems: mockReceivableSnapshots.length,
    totalPage: 1,
  };
}

export async function getMockPayments(params?: {
  page?: number;
  limit?: number;
  status?: string;
}): Promise<{
  data: CreatorPayment[];
  page: number;
  limit: number;
  totalItems: number;
  totalPage: number;
}> {
  await delay(MOCK_DELAY_MS);
  const page = params?.page ?? 1;
  const limit = params?.limit ?? 10;
  let list = [...mockPayments];
  if (params?.status && params.status !== "all") {
    list = list.filter((p) => p.status === params.status);
  }
  const totalItems = list.length;
  const totalPage = Math.max(1, Math.ceil(totalItems / limit));
  const start = (page - 1) * limit;
  const data = list.slice(start, start + limit);
  return {
    data,
    page,
    limit,
    totalItems,
    totalPage,
  };
}
